import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router";
import type { z } from "zod";

import { Alert, Button, Card, LoadingBlock, TextField } from "../../components/ui";
import { api, applyFieldErrors, errorMessage } from "../../lib/api";
import type { Message } from "../../lib/types";
import { emailOnlySchema, PASSWORD_MIN, resetSchema } from "./schemas";

/** Remove one-time tokens from the address bar and browser history right away. */
function useOneTimeToken(): string | null {
  const [params] = useSearchParams();
  const [token] = useState(() => params.get("token"));
  useEffect(() => {
    if (token) window.history.replaceState(null, "", window.location.pathname);
  }, [token]);
  return token;
}

export function VerifyEmailPage() {
  const token = useOneTimeToken();
  const [state, setState] = useState<{ ok: boolean; message: string } | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    api
      .post<Message>("/auth/verify-email", { token })
      .then((r) => setState({ ok: true, message: r.message }))
      .catch((err) => setState({ ok: false, message: errorMessage(err) }));
  }, [token]);

  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-4 text-2xl font-bold">Confirm your email</h1>
      {!token && (
        <Alert tone="error">
          This link is missing information. Please use the link from your email.
        </Alert>
      )}
      {token && !state && <LoadingBlock label="Confirming" />}
      {state && <Alert tone={state.ok ? "success" : "error"}>{state.message}</Alert>}
      <p className="mt-4">
        <Link to="/login" className="text-brand-700 underline">
          Go to sign in
        </Link>
      </p>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof emailOnlySchema>>({ resolver: zodResolver(emailOnlySchema) });

  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-4 text-2xl font-bold">Reset your password</h1>
      <Card>
        {done ? (
          <Alert tone="success">{done}</Alert>
        ) : (
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(async (v) => {
              setError(null);
              try {
                setDone((await api.post<Message>("/auth/forgot-password", v)).message);
              } catch (err) {
                setError(errorMessage(err));
              }
            })}
          >
            <p className="text-sm text-ink-muted">
              Enter the email you use to sign in. We will send you a link to choose a new password.
            </p>
            {error && <Alert tone="error">{error}</Alert>}
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              error={errors.email?.message}
              {...register("email")}
            />
            <Button type="submit" busy={isSubmitting}>
              Send reset link
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}

export function ResetPasswordPage() {
  const token = useOneTimeToken();
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    setError: setFieldError,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof resetSchema>>({ resolver: zodResolver(resetSchema) });

  if (!token)
    return (
      <div className="mx-auto max-w-md">
        <Alert tone="error">
          This reset link is missing information. Please use the link from your email.
        </Alert>
      </div>
    );

  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-4 text-2xl font-bold">Choose a new password</h1>
      <Card>
        {done ? (
          <>
            <Alert tone="success">{done}</Alert>
            <p className="mt-4">
              <Link to="/login" className="text-brand-700 underline">
                Sign in
              </Link>
            </p>
          </>
        ) : (
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(async (v) => {
              setError(null);
              try {
                setDone(
                  (await api.post<Message>("/auth/reset-password", { token, password: v.password }))
                    .message,
                );
              } catch (err) {
                if (!applyFieldErrors(err, setFieldError)) setError(errorMessage(err));
              }
            })}
          >
            {error && <Alert tone="error">{error}</Alert>}
            <TextField
              label="New password"
              type="password"
              autoComplete="new-password"
              required
              hint={`At least ${PASSWORD_MIN} characters.`}
              error={errors.password?.message}
              {...register("password")}
            />
            <TextField
              label="Repeat new password"
              type="password"
              autoComplete="new-password"
              required
              error={errors.confirm_password?.message}
              {...register("confirm_password")}
            />
            <Button type="submit" busy={isSubmitting}>
              Save new password
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
